class OSProductEditorialGallery extends HTMLElement {
  constructor() {
    super();
    this.osCurrentIndex = 0;
    this.osTransitionDuration = 450;
    this.osIsAnimating = false;
  }

  connectedCallback() {
    this.osTrack = this.querySelector('.os-product-editorial-gallery__track');
    this.osSlides = Array.from(this.querySelectorAll('.os-product-editorial-gallery__slide'));
    this.osPreviousButton = this.querySelector('[data-os-gallery-previous]');
    this.osNextButton = this.querySelector('[data-os-gallery-next]');
    this.osStatus = this.querySelector('[data-os-gallery-status]');

    if (!this.osTrack || this.osSlides.length === 0) return;

    const osInitialMediaIndex = this.osSlides.findIndex(
      (osSlide) => String(osSlide.dataset.osMediaId) === String(this.dataset.initialMediaId)
    );
    this.osCurrentIndex = osInitialMediaIndex >= 0 ? osInitialMediaIndex : 0;

    this.osCreateLoopClones();
    this.osSetTrackPosition(this.osCurrentIndex + 1, false);
    this.osUpdateSlideState();

    this.osPreviousButton?.addEventListener('click', this.osShowPrevious);
    this.osNextButton?.addEventListener('click', this.osShowNext);
    this.addEventListener('keydown', this.osHandleKeydown);

    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.variantChange) {
      this.osVariantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.variantChange,
        this.osHandleVariantChange
      );
    }
  }

  disconnectedCallback() {
    this.osPreviousButton?.removeEventListener('click', this.osShowPrevious);
    this.osNextButton?.removeEventListener('click', this.osShowNext);
    this.removeEventListener('keydown', this.osHandleKeydown);
    this.osVariantChangeUnsubscriber?.();
  }

  osCreateLoopClones() {
    if (this.osSlides.length < 2) return;

    const osFirstSlide = this.osSlides[0].cloneNode(true);
    const osSecondSlide = (this.osSlides[1] || this.osSlides[0]).cloneNode(true);
    const osLastSlide = this.osSlides[this.osSlides.length - 1].cloneNode(true);
    const osBeforeLastSlide = (
      this.osSlides[this.osSlides.length - 2] || this.osSlides[this.osSlides.length - 1]
    ).cloneNode(true);

    [osBeforeLastSlide, osLastSlide, osFirstSlide, osSecondSlide].forEach((osSlide) => {
      osSlide.dataset.osClone = 'true';
      osSlide.setAttribute('aria-hidden', 'true');
    });

    this.osTrack.prepend(osBeforeLastSlide, osLastSlide);
    this.osTrack.append(osFirstSlide, osSecondSlide);
  }

  osShowPrevious = () => {
    if (this.osIsAnimating || this.osSlides.length < 2) return;

    if (this.osCurrentIndex === 0) {
      this.osAnimateTo(0, () => {
        this.osCurrentIndex = this.osSlides.length - 1;
        this.osSetTrackPosition(this.osCurrentIndex + 1, false);
        this.osUpdateSlideState();
      });
      return;
    }

    this.osCurrentIndex -= 1;
    this.osAnimateTo(this.osCurrentIndex + 1, this.osUpdateSlideState);
  };

  osShowNext = () => {
    if (this.osIsAnimating || this.osSlides.length < 2) return;

    if (this.osCurrentIndex === this.osSlides.length - 1) {
      this.osAnimateTo(this.osSlides.length + 1, () => {
        this.osCurrentIndex = 0;
        this.osSetTrackPosition(1, false);
        this.osUpdateSlideState();
      });
      return;
    }

    this.osCurrentIndex += 1;
    this.osAnimateTo(this.osCurrentIndex + 1, this.osUpdateSlideState);
  };

  osAnimateTo(osTrackIndex, osAfterTransition) {
    this.osIsAnimating = true;
    this.osSetTrackPosition(osTrackIndex, true);

    window.setTimeout(() => {
      this.osIsAnimating = false;
      osAfterTransition?.();
    }, this.osTransitionDuration);
  }

  osSetTrackPosition(osTrackIndex, osAnimate) {
    this.osTrack.style.setProperty('--os-editorial-track-index', osTrackIndex);
    this.osTrack.classList.toggle('is-animated', osAnimate);
  }

  osUpdateSlideState = () => {
    this.osSlides.forEach((osSlide, osIndex) => {
      const osIsCurrent = osIndex === this.osCurrentIndex;
      osSlide.classList.toggle('is-current', osIsCurrent);
      osSlide.setAttribute('aria-hidden', osIsCurrent ? 'false' : 'true');
    });

    if (this.osStatus) {
      this.osStatus.textContent = `${this.osCurrentIndex + 1} / ${this.osSlides.length}`;
    }
  };

  osHandleKeydown = (osEvent) => {
    if (osEvent.key === 'ArrowLeft') this.osShowPrevious();
    if (osEvent.key === 'ArrowRight') this.osShowNext();
  };

  osHandleVariantChange = ({ data }) => {
    if (String(data?.sectionId) !== String(this.dataset.sectionId)) return;

    const osFeaturedMediaId = data?.variant?.featured_media?.id;
    if (!osFeaturedMediaId) return;

    const osVariantIndex = this.osSlides.findIndex(
      (osSlide) => String(osSlide.dataset.osMediaId) === String(osFeaturedMediaId)
    );

    if (osVariantIndex < 0 || osVariantIndex === this.osCurrentIndex) return;

    this.osCurrentIndex = osVariantIndex;
    this.osSetTrackPosition(this.osCurrentIndex + 1, true);
    this.osUpdateSlideState();
  };
}

if (!customElements.get('os-product-editorial-gallery')) {
  customElements.define('os-product-editorial-gallery', OSProductEditorialGallery);
}
